import apiService from './api';
import type { ApiResponse } from '../types';
import type {
  Call, CallerLookup, PhoneDomain, PhoneDomainWithSecret, PhoneExtension, SalesPerson, SalesSettings,
  SaveExtensionPayload, Voicemail, VoicemailFilter,
} from '../types/sales';

/**
 * The Sales module: the phone system connection, caller lookup and the call
 * log. All under /api/sales (HHNodeServer routes/sales.js).
 */
class SalesService {
  // ── Phone system ──

  async listDomains(): Promise<ApiResponse<PhoneDomain[]>> {
    return apiService.get('/sales/phone/domains');
  }

  async addDomain(label: string, domainName: string): Promise<ApiResponse<PhoneDomainWithSecret>> {
    return apiService.post('/sales/phone/domains', { label, domain_name: domainName });
  }

  async updateDomain(id: number, changes: { label?: string; active?: boolean }): Promise<ApiResponse<PhoneDomain>> {
    return apiService.put(`/sales/phone/domains/${id}`, changes);
  }

  /** A new secret. The bridge stops being accepted until it has the new one. */
  async rotateSecret(id: number): Promise<ApiResponse<PhoneDomainWithSecret>> {
    return apiService.post(`/sales/phone/domains/${id}/rotate-secret`, {});
  }

  async listExtensions(domainId: number): Promise<ApiResponse<PhoneExtension[]>> {
    return apiService.get(`/sales/phone/domains/${domainId}/extensions`);
  }

  async addExtension(domainId: number, payload: SaveExtensionPayload): Promise<ApiResponse<{ id: number }>> {
    return apiService.post(`/sales/phone/domains/${domainId}/extensions`, payload);
  }

  async updateExtension(id: number, payload: SaveExtensionPayload): Promise<ApiResponse<{ id: number }>> {
    return apiService.put(`/sales/phone/extensions/${id}`, payload);
  }

  async deleteExtension(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/sales/phone/extensions/${id}`);
  }

  async listPeople(): Promise<ApiResponse<SalesPerson[]>> {
    return apiService.get('/sales/phone/people');
  }

  // ── Callers and calls ──

  async lookup(number: string): Promise<ApiResponse<CallerLookup>> {
    return apiService.get(`/sales/phone/lookup?number=${encodeURIComponent(number)}`);
  }

  /** Switch recording on (callers must be told first) or off. */
  async setRecording(domainId: number, enabled: boolean, callersTold = false): Promise<ApiResponse<PhoneDomain>> {
    return apiService.put(`/sales/phone/domains/${domainId}/recording`, enabled ? { enabled, callers_told: callersTold } : { enabled });
  }

  async getSettings(): Promise<ApiResponse<SalesSettings>> {
    return apiService.get('/sales/settings');
  }

  async saveSettings(retentionMonths: number | null): Promise<ApiResponse<SalesSettings>> {
    return apiService.put('/sales/settings', { retention_months: retentionMonths });
  }

  async listCalls(opts: { customerId?: number; limit?: number } = {}): Promise<ApiResponse<Call[]>> {
    const q = new URLSearchParams();
    if (opts.customerId) q.set('customer_id', String(opts.customerId));
    if (opts.limit) q.set('limit', String(opts.limit));
    const qs = q.toString();
    return apiService.get(`/sales/calls${qs ? `?${qs}` : ''}`);
  }

  /** A recording's audio, for someone who may hear it. */
  async callRecording(callId: number): Promise<Blob> {
    return apiService.get(`/sales/calls/${callId}/recording`, { responseType: 'blob', timeout: 60000 }) as unknown as Promise<Blob>;
  }

  // ── Voicemail ──

  async listVoicemails(filter: VoicemailFilter = 'open', customerId?: number): Promise<ApiResponse<Voicemail[]>> {
    const q = new URLSearchParams({ filter });
    if (customerId) q.set('customer_id', String(customerId));
    return apiService.get(`/sales/voicemails?${q.toString()}`);
  }

  async assignVoicemail(id: number, userId: number | null): Promise<ApiResponse<Voicemail>> {
    return apiService.put(`/sales/voicemails/${id}/assign`, { user_id: userId });
  }

  async followUpVoicemail(id: number, note: string): Promise<ApiResponse<Voicemail>> {
    return apiService.post(`/sales/voicemails/${id}/follow-up`, { note });
  }

  async reopenVoicemail(id: number): Promise<ApiResponse<Voicemail>> {
    return apiService.post(`/sales/voicemails/${id}/reopen`, {});
  }

  async voicemailAudio(id: number): Promise<Blob> {
    return apiService.get(`/sales/voicemails/${id}/audio`, { responseType: 'blob', timeout: 60000 }) as unknown as Promise<Blob>;
  }

  /** The people with a Sales seat, to give a voicemail to. */
  async listSalesPeople(): Promise<ApiResponse<SalesPerson[]>> {
    return apiService.get('/sales/people');
  }
}

const salesService = new SalesService();
export default salesService;
